import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  MapPin, Phone, Envelope, User, House, ArrowLeft, CheckCircle, XCircle, Clock,
  Receipt, Package, ChatsCircle, Warning, Note, Pulse, ShieldCheck, Leaf,
  Truck, IdentificationCard, PencilSimple, Plus, Trash, Coins, Handshake, ArrowSquareOut,
  Ticket as TicketIcon,
} from "@phosphor-icons/react";
import CustomerForm from "./CustomerForm";
import { EditTicketModal } from "../crm/Tickets";
import SmartDataTab from "./SmartDataTab";
import AiAnalyticsTab from "./AiAnalyticsTab";
import { WalletRefunds } from "../../components/payments/WalletRefunds";
import { ChatCircleText } from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";

const STATUS_ICON = { approved: CheckCircle, pending: Clock, rejected: XCircle };

const TABS = [
  { key: "overview", label: "Overview", icon: User },
  { key: "smart", label: "Smart Data (AI)", icon: Pulse },
  { key: "ai360", label: "AI Service Analytics", icon: Pulse },
  { key: "timeline", label: "Activity Timeline", icon: Pulse },
  { key: "services", label: "Service History", icon: Truck },
  { key: "waste", label: "Waste Analytics", icon: Leaf },
  { key: "payments", label: "Payment Behaviour", icon: Receipt },
  { key: "qr", label: "QR Bag Tracking", icon: Package },
  { key: "complaints", label: "Complaints & Issues", icon: ChatsCircle },
  { key: "risk", label: "Risk & Compliance", icon: ShieldCheck },
  { key: "rewards", label: "Rewards", icon: Coins },
  { key: "comms", label: "Communication Logs", icon: Envelope },
  { key: "notes", label: "Notes & Actions", icon: Note },
];
const SMART_VIEW_ROLES = ["superadmin", "admin", "manager"];

export default function CustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canSeeSmart = SMART_VIEW_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [specialZones, setSpecialZones] = useState([]);
  const [specialDates, setSpecialDates] = useState([]);
  // Iter 116 — open a ticket directly from the Complaints tab
  const [openTicket, setOpenTicket] = useState(null);
  // Iter 160 — reject-with-reason modal
  const [rejectModal, setRejectModal] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/auth/users").then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const saveTicketEdit = async (patch) => {
    if (!openTicket) return;
    try {
      await api.put(`/tickets/${openTicket.id}`, patch);
      toast.success("Ticket updated");
      setOpenTicket(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [loadError, setLoadError] = useState("");
  const load = async () => {
    setLoadError("");
    try { const r = await api.get(`/customers/${id}/profile`); setData(r.data); }
    catch (e) { setLoadError(formatApiError(e)); toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    load();
    // Load the customer's special-health-zone memberships + any upcoming
    // matching special-collection dates so we can show a chip badge.
    api.get(`/customers/${id}/special-zones`)
      .then(r => setSpecialZones(r.data?.zones || []))
      .catch(() => setSpecialZones([]));
    api.get(`/customers/${id}/special-collection-dates`)
      .then(r => setSpecialDates(r.data?.rules || []))
      .catch(() => setSpecialDates([]));
  }, [id]);

  const verify = async (decision, extra = {}) => {
    try {
      await api.post(`/customers/${id}/verify`, { decision, ...extra });
      toast.success(`Customer ${decision}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return (
    <AppShell title="Customer" subtitle={loadError ? "Couldn't load" : "Loading"}>
      {loadError ? (
        <div className="p-6 flex items-center gap-3 text-sm text-[#7A2E18]" data-testid="cust-profile-error">
          <span>Couldn't load customer details: {loadError}</span>
          <button onClick={load} className="h-8 px-3 border border-[#7A2E18] rounded-md font-bold" data-testid="cust-profile-retry">Retry</button>
        </div>
      ) : <div className="p-6">Loading…</div>}
    </AppShell>
  );

  const c = data.customer;
  const Icon = STATUS_ICON[c.status] || Clock;

  return (
    <AppShell
      title={c.name}
      subtitle="360° Customer profile"
      actions={
        <div className="flex gap-2">
          <Link to={`/crm/chats?start=${c.id}`} data-testid="btn-message-customer"
                className="h-10 px-3 rounded-md text-sm bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold inline-flex items-center gap-1.5">
            <ChatCircleText size={14} weight="fill" />Message customer
          </Link>
          <button data-testid="btn-edit-customer" onClick={() => setEditOpen(true)}
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <PencilSimple size={12} />Edit
          </button>
          <Link to="/customers" className="h-10 px-3 rounded-md text-sm text-[#5C5A55] inline-flex items-center gap-1 hover:bg-[#F7F6F2]">
            <ArrowLeft size={12} />Back
          </Link>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left column — identity */}
        <div className="lg:col-span-1 bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-[#D4E0D1] flex items-center justify-center">
              <User size={26} color="#2D5A27" weight="fill" />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-xl truncate" data-testid="cd-name">{c.name}</div>
              {c.customer_code && (
                <div className="font-mono text-[11px] text-[#2D5A27] tracking-wider mt-0.5" data-testid="cd-code">
                  {c.customer_code}
                </div>
              )}
              {/* User request: show LSGD ward + LSGD name immediately after
                  customer id so identity + jurisdiction are visible at a glance. */}
              {(c.ward_name || c.ward_id) && (
                <div className="text-[11px] text-[#5C5A55] mt-1" data-testid="cd-ward-line">
                  Ward: <span className="text-[#1A1A1A] font-medium">
                    {c.ward_name || <span className="font-mono text-[10px]">{c.ward_id}</span>}
                  </span>
                </div>
              )}
              {(c.lsgd_name || c.lsgd_id) && (
                <div className="text-[11px] text-[#5C5A55]" data-testid="cd-lsgd-line">
                  LSGD: <span className="text-[#1A1A1A] font-medium">
                    {c.lsgd_name || <span className="font-mono text-[10px]">{c.lsgd_id}</span>}
                  </span>
                </div>
              )}
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mt-1">{c.customer_type}</div>
            </div>
          </div>
          <div className={`mt-4 inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] uppercase tracking-[0.2em] ${
            c.status === "approved" ? "bg-[#D4E0D1] text-[#1A3317]" :
            c.status === "pending" ? "bg-[#F9E4B7] text-[#7A5A11]" :
            "bg-[#F4C7B9] text-[#7A2E18]"
          }`}>
            <Icon size={11} weight="fill" /> {c.status}
          </div>

          {/* Special Health Zone membership + upcoming special collection */}
          {(specialZones.length > 0 || specialDates.length > 0) && (
            <div className="mt-4 border-t border-[#E5E2D9] pt-3" data-testid="cd-special-zones">
              {specialZones.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {specialZones.map(sz => (
                    <span key={sz.id}
                          data-testid={`cd-sz-${sz.id}`}
                          className="inline-flex items-center gap-1 bg-[#F0EDF7] border border-[#3B2C7F]/25 text-[#3B2C7F] px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.1em]">
                      <span className="text-[#3B2C7F]">◆</span> {sz.name}
                    </span>
                  ))}
                </div>
              )}
              {specialDates.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Upcoming Special Collections</div>
                  {specialDates.slice(0, 3).map(r => (
                    <div key={r.id} className="text-[11px] text-[#1A1A1A]" data-testid={`cd-scd-${r.id}`}>
                      <span className="font-medium">{r.title}</span>
                      <span className="ml-1 text-[#2D5A27] font-mono">→ {r.dates[0]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 space-y-3 text-sm">
            <InfoRow icon={Phone} label="Phone" value={c.phone} />
            {c.alternate_phone && <InfoRow icon={Phone} label="Alt phone" value={c.alternate_phone} />}
            {c.email && <InfoRow icon={Envelope} label="Email" value={c.email} />}
            <InfoRow icon={MapPin} label="GPS" value={`${c.latitude?.toFixed(5)}, ${c.longitude?.toFixed(5)}`} />
            <InfoRow icon={House} label="Address"
                      value={[c.house_no, c.house_name, c.landmark, c.address].filter(Boolean).join(", ") || "—"} />
            {c.haritha_mithram_id && <InfoRow icon={IdentificationCard} label="Haritha ID" value={c.haritha_mithram_id} />}
            {c.vehicle_type && <InfoRow icon={Truck} label="Vehicle"
              value={c.vehicle_type.replace(/_/g, " ").replace("6 wheeler truck", "6-wheeler truck")} />}
          </div>

          <div className="mt-6 pt-6 border-t border-[#E5E2D9] grid grid-cols-2 gap-3 text-center">
            <Kpi label="Services" value={data.services.counts.completed || 0} sub="completed" />
            <Kpi label="CO₂ kg" value={data.waste.total_co2_kg || 0} sub="saved" />
            <Kpi label="Paid" value={`₹${data.payments.total_paid}`} sub={`${data.payments.paid_count} invoices`} />
            <Kpi label="Due" value={`₹${data.payments.total_due}`} sub={`${data.payments.unpaid_count} unpaid`}
                  danger={data.payments.total_due > 0} />
          </div>

          {/* Iter 86 — verification-audit strip: who approved / rejected and when.
              Uses fields already stamped by POST /customers/{id}/verify. */}
          {(c.approved_by_name || c.rejected_by_name) && (
            <div className="mt-6 pt-6 border-t border-[#E5E2D9]" data-testid="verify-audit-strip">
              {c.approved_by_name && (
                <div className="rounded-md bg-[#F0F7EE] border border-[#D4E0D1] px-3 py-2 text-xs text-[#1A3317] flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-[#2D5A27]"/>
                  <div className="flex-1 min-w-0">
                    <div><strong>Approved by</strong> {c.approved_by_name}</div>
                    {c.approved_at && (
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] mt-0.5">
                        {new Date(c.approved_at).toLocaleString()}
                      </div>
                    )}
                    {c.verify_note && (
                      <div className="text-[11px] text-[#5C5A55] mt-1 italic">"{c.verify_note}"</div>
                    )}
                  </div>
                </div>
              )}
              {c.rejected_by_name && (
                <div className="rounded-md bg-[#F9EDE7] border border-[#F4C7B9] px-3 py-2 text-xs text-[#7A2E18] flex items-start gap-2">
                  <XCircle size={14} weight="fill" className="mt-0.5 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div><strong>Rejected by</strong> {c.rejected_by_name}</div>
                    {c.rejected_at && (
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] mt-0.5">
                        {new Date(c.rejected_at).toLocaleString()}
                      </div>
                    )}
                    {c.rejected_reason_code && (
                      <div className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded-full bg-[#7A2E18] text-white text-[9px] uppercase tracking-[0.14em] font-semibold"
                           data-testid="rejection-reason-code">
                        {c.rejected_reason_code.replace(/_/g, " ")}
                      </div>
                    )}
                    {(c.rejected_reason || c.verify_note) && (
                      <div className="text-[11px] text-[#5C5A55] mt-1 italic">
                        &ldquo;{c.rejected_reason || c.verify_note}&rdquo;
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {(c.status === "pending" || c.status === "pending_verification") && (
            <div className="mt-6 pt-6 border-t border-[#E5E2D9] space-y-2">
              <button data-testid="detail-approve" onClick={() => verify("approved")}
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <CheckCircle size={14} weight="bold"/>Approve
              </button>
              <button data-testid="detail-reject" onClick={() => setRejectModal(true)}
                      className="w-full h-10 border border-[#C25D38] text-[#C25D38] rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#C25D38]/8">
                <XCircle size={14} weight="bold"/>Reject
              </button>
            </div>
          )}

          {/* Iter 158 — Superadmin override for rejected customers.
              Lets a superadmin re-open, edit, and approve a row that
              staff already rejected — needed when the rejection was a
              mistake or the underlying issue has been fixed. */}
          {c.status === "rejected" && user?.role === "superadmin" && (
            <div className="mt-6 pt-6 border-t border-[#E5E2D9] space-y-2"
                 data-testid="detail-override-block">
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#7A2E18] font-semibold">
                Superadmin override
              </div>
              <div className="text-[11px] text-[#5C5A55]">
                This customer was rejected
                {c.rejected_by_name && <> by <strong>{c.rejected_by_name}</strong></>}
                {c.rejected_reason && <> — &ldquo;{c.rejected_reason}&rdquo;</>}.
                You can re-edit their details or override the rejection and
                mark them active.
              </div>
              <button data-testid="detail-override-edit"
                      onClick={() => setEditOpen(true)}
                      className="w-full h-10 border border-[#0B2B66] text-[#0B2B66] rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#E4EAF5]">
                <PencilSimple size={14} weight="bold"/>Re-edit details
              </button>
              <button data-testid="detail-override-approve"
                      onClick={async () => {
                        if (!window.confirm(
                          `Override the rejection and approve ${c.name}?`
                        )) return;
                        try {
                          await api.post(`/customers/${id}/verify`, {
                            decision: "approved",
                            note: "Superadmin override — rejection reversed",
                          });
                          toast.success("Rejection overridden — customer approved");
                          load();
                        } catch (e) { toast.error(formatApiError(e)); }
                      }}
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <CheckCircle size={14} weight="bold"/>Override & Approve
              </button>
            </div>
          )}
        </div>

        {/* Right — tabs */}
        <div className="lg:col-span-3 bg-white border border-[#E5E2D9] rounded-md">
          <div className="flex overflow-x-auto border-b border-[#E5E2D9]">
            {TABS.filter(t => !["smart", "ai360"].includes(t.key) || canSeeSmart).map(t => (
              <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                      className={`px-4 py-3 text-xs shrink-0 border-b-2 transition-colors inline-flex items-center gap-1.5 ${tab === t.key ? "border-[#2D5A27] text-[#1A1A1A] font-medium" : "border-transparent text-[#5C5A55] hover:text-[#1A1A1A]"}`}>
                <t.icon size={13} weight="duotone" /> {t.label}
              </button>
            ))}
          </div>
          <div className="p-6" data-testid={`tab-content-${tab}`}>
            {tab === "overview" && <OverviewTab c={c} data={data} />}
            {tab === "smart" && canSeeSmart && <SmartDataTab customerId={id} canEdit={user?.role === "superadmin"}/>}
            {tab === "ai360" && canSeeSmart && <AiAnalyticsTab customerId={id} customerName={c.name} />}
            {tab === "timeline" && <TimelineTab items={data.activity} />}
            {tab === "services" && <ServicesTab services={data.services} />}
            {tab === "waste" && <WasteTab waste={data.waste} />}
            {tab === "payments" && <PaymentsTab payments={data.payments} customerId={c.id} />}
            {tab === "qr" && <QrTab bags={data.qr_bags} customerId={c.id} />}
            {tab === "complaints" && <ComplaintsTab tickets={data.complaints} onOpen={setOpenTicket} />}
            {tab === "risk" && <RiskTab risk={data.risk} />}
            {tab === "rewards" && <RewardsTab customerId={id} />}
            {tab === "comms" && <CommsTab items={data.communications} />}
            {tab === "notes" && <NotesTab customerId={id} notes={data.notes} reload={load} />}
          </div>
        </div>
      </div>

      {editOpen && (
        <CustomerForm
          initial={c}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}

      {/* Iter 116 — inline ticket edit + reply modal from Complaints tab */}
      {openTicket && (
        <EditTicketModal
          ticket={openTicket}
          users={users}
          onClose={() => setOpenTicket(null)}
          onSave={saveTicketEdit}
        />
      )}

      {/* Iter 160 — Reject with reason picker */}
      {rejectModal && (
        <RejectReasonModal
          customerName={c.name}
          onClose={() => setRejectModal(false)}
          onConfirm={(reason_code, note) => {
            setRejectModal(false);
            verify("rejected", { reason_code, note });
          }}
        />
      )}
    </AppShell>
  );
}

// ---------- Iter 160 · Reject Reason picker ----------
// Forces staff to pick a canonical reason before a rejection is sent,
// so ops can filter rejections by cause and spot trends (bad_photos
// spiking = camera guidance in the app needs work).
function RejectReasonModal({ customerName, onClose, onConfirm }) {
  const [reasons, setReasons] = useState([]);
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/customers/rejection-reasons")
      .then(r => setReasons(r.data || []))
      .catch(() => toast.error("Could not load reasons"));
  }, []);
  const submit = () => {
    if (!code) { toast.error("Pick a reason before rejecting"); return; }
    if (code === "other" && !note.trim()) {
      toast.error("Add a note explaining the reason"); return;
    }
    setBusy(true);
    onConfirm(code, note.trim() || null);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="reject-reason-modal">
      <div className="bg-white rounded-lg border border-[#E5E2D9] w-full max-w-md p-5 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#7A2E18] font-semibold">
            Reject customer
          </div>
          <div className="font-heading text-lg mt-1">{customerName}</div>
          <div className="text-xs text-[#5C5A55] mt-1">
            Pick the reason. This helps us track why customers get
            rejected and improve onboarding.
          </div>
        </div>
        <div className="space-y-1.5">
          {reasons.map(r => (
            <label key={r.code}
                   data-testid={`reject-reason-${r.code}`}
                   className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer ${
                     code === r.code
                       ? "border-[#7A2E18] bg-[#FBEBE5]"
                       : "border-[#E5E2D9] hover:bg-[#F7F6F2]"
                   }`}>
              <input type="radio" name="reject-reason"
                     value={r.code}
                     checked={code === r.code}
                     onChange={() => setCode(r.code)}
                     className="accent-[#7A2E18]"/>
              <span className="text-sm">{r.label}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
            Note {code === "other" ? "(required)" : "(optional)"}
          </label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
                    rows={2}
                    data-testid="reject-reason-note"
                    placeholder="Add context for the audit trail…"
                    className="mt-1 w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm resize-none focus:border-[#7A2E18] outline-none"/>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose}
                  data-testid="reject-reason-cancel"
                  className="h-9 px-3 rounded-md border border-[#E5E2D9] text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !code}
                  data-testid="reject-reason-confirm"
                  className="h-9 px-3 rounded-md bg-[#7A2E18] hover:bg-[#5C2113] disabled:opacity-60 text-white text-sm font-medium">
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Tabs ----------

const RegistrationSourceBadge = ({ c }) => {
  // Compute source defensively when registration_source is not stamped on doc.
  const src = c.registration_source
    || (c.referred_by_customer_id ? "affiliate"
       : c.partner_id ? "partner" : "direct");
  if (src === "affiliate") {
    return (
      <span data-testid="reg-source-badge"
             className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#D4E0D1] text-[#2D5A27]">
        <span>Customer Referral</span>
      </span>
    );
  }
  if (src === "partner") {
    return (
      <span data-testid="reg-source-badge"
             className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#E5DAB7] text-[#7A5A11]">
        <span>Partner</span>
      </span>
    );
  }
  return (
    <span data-testid="reg-source-badge"
           className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#F7F6F2] text-[#5C5A55] border border-[#E5E2D9]">
      <span>Direct Registration</span>
    </span>
  );
};

const OverviewTab = ({ c, data }) => {
  const labelVT = (v) => v ? v.replace(/_/g, " ") : "—";
  const labelCat = (v) => v === "drop" ? "Drop"
    : v === "home_service" ? "Home collection"
    : v === "both" ? "Both" : "—";
  const labelSvc = (v) => v ? v.replace(/_/g, " ") : "active";
  return (
    <div className="space-y-6" data-testid="overview-body">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">Registration</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Cell label="Registered on">{c.registered_at ? new Date(c.registered_at).toLocaleString() : c.created_at ? new Date(c.created_at).toLocaleString() : "—"}</Cell>
          <Cell label="Registered by">
            {c.registered_by_type === "self" ? "Self (public registration)"
              : (c.registered_by_name || c.registered_by_type || "—")}
            {c.registered_by_type && c.registered_by_type !== "self" && (
              <span className="text-[10px] text-[#8A8780] ml-1 capitalize">· {c.registered_by_type}</span>
            )}
          </Cell>
          <Cell label="Approved on">{c.approved_at ? new Date(c.approved_at).toLocaleString() : "—"}</Cell>
          <Cell label="Approved by">{c.approved_by_name || "—"}</Cell>
        </div>
      </div>
      <div data-testid="registration-details-block">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">Registration details</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Cell label="Registration source">
            <RegistrationSourceBadge c={c} />
          </Cell>
          {c.referred_by_customer_id && (
            <Cell label="Referred by">
              <Link to={`/customers/${c.referred_by_customer_id}`}
                     data-testid="referred-by-link"
                     className="inline-flex items-center gap-1 text-[#2D5A27] hover:underline">
                {c.referred_by_customer_name || "Customer"}
                {c.referred_by_customer_code && (
                  <span className="font-mono text-[10px] text-[#8A8780]">· {c.referred_by_customer_code}</span>
                )}
                <ArrowSquareOut size={11}/>
              </Link>
            </Cell>
          )}
          {c.partner_id && (
            <Cell label="Registered via partner">
              <Link to={`/customers/referrals/partners/${c.partner_id}`}
                     data-testid="partner-link"
                     className="inline-flex items-center gap-1 text-[#2D5A27] hover:underline">
                <Handshake size={12} weight="fill"/>
                {c.partner_name || "Partner"}
                {c.partner_type && (
                  <span className="font-mono text-[10px] text-[#8A8780]">· {c.partner_type}</span>
                )}
                <ArrowSquareOut size={11}/>
              </Link>
            </Cell>
          )}
        </div>
      </div>
      <div data-testid="location-block">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">Location & Health Zone</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Cell label="State">{c.state_name || (c.state_id ? <span className="font-mono text-[10px]">{c.state_id}</span> : "—")}</Cell>
          <Cell label="District">{c.district_name || (c.district_id ? <span className="font-mono text-[10px]">{c.district_id}</span> : "—")}</Cell>
          <Cell label="LSGD">{c.lsgd_name || (c.lsgd_id ? <span className="font-mono text-[10px]">{c.lsgd_id}</span> : "—")}</Cell>
          <Cell label="Ward">{c.ward_name || (c.ward_id ? <span className="font-mono text-[10px]">{c.ward_id}</span> : "—")}</Cell>
          <Cell label="Health zone">
            {c.zone_name ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#D4E0D1] text-[#2D5A27]">
                {c.zone_name}
              </span>
            ) : c.zone_id ? <span className="font-mono text-[10px]">{c.zone_id}</span>
              : <span className="text-[10px] text-[#7A2E18] bg-[#F4C7B9] px-2 py-0.5 rounded uppercase tracking-wider">No zone mapped</span>}
          </Cell>
          <Cell label="Collection schedule">
            {(c.collection_schedule_days || []).length === 0 ? "—" : (
              <span className="capitalize text-[#5C5A55]">
                {(c.collection_schedule_days || []).join(", ")}
              </span>
            )}
          </Cell>
          <Cell label="Address">{c.address || "—"}{c.house_no ? ` · #${c.house_no}` : ""}{c.house_name ? ` · ${c.house_name}` : ""}</Cell>
          <Cell label="Coordinates">
            {c.latitude && c.longitude
              ? <a href={`https://www.google.com/maps?q=${c.latitude},${c.longitude}`}
                    target="_blank" rel="noopener" className="text-[#2D5A27] hover:underline font-mono text-[11px]">
                  {Number(c.latitude).toFixed(5)}, {Number(c.longitude).toFixed(5)} ↗
                </a>
              : "—"}
          </Cell>
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">Service preferences</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Cell label="Preferred category">{labelCat(c.preferred_service_category)}</Cell>
          {/* Iter 78 — Primary waste (D-BMW) chips shown read-only. */}
          <Cell label="Primary waste">{
            (c.primary_waste_names || []).length === 0
              ? <span className="text-[#8A8780]">— not set —</span>
              : (
                <div className="flex flex-wrap gap-1" data-testid="cd-primary-waste">
                  {(c.primary_waste_names || []).map(n => (
                    <span key={n}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-[#F9F0D6] border border-[#F5C518]/50 text-[#7A5A11] font-semibold">
                      {n}
                    </span>
                  ))}
                </div>
              )
          }</Cell>
          <Cell label="Pickup schedule">{
            (() => {
              const freq = c.auto_schedule_frequency || "on_demand";
              const label = {
                on_demand: "On-demand only",
                weekly: "Every week",
                alt_weekly: "Alternate week",
                monthly: "Monthly once",
              }[freq] || freq;
              const tone = freq === "on_demand"
                ? "bg-[#F7F6F2] text-[#5C5A55]"
                : "bg-[#DDE9F6] text-[#0D47A1]";
              return (
                <span className={`inline-block text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${tone}`}
                      data-testid="cd-pickup-schedule">
                  {label}
                </span>
              );
            })()
          }</Cell>
          <Cell label="Package">{
            c.package_id
              ? (data.customer?.package_snapshot?.name || <span className="font-mono text-xs">{c.package_id}</span>)
              : "—"
          }</Cell>
          <Cell label="Service status">
            <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded capitalize ${
              c.service_status === "active" ? "bg-[#D4E0D1] text-[#1A3317]" :
              c.service_status === "inactive" ? "bg-[#E5E2D9] text-[#5C5A55]" :
              c.service_status === "temporarily_unavailable" ? "bg-[#F9E4B7] text-[#7A5A11]" :
              c.service_status === "stopped" ? "bg-[#F4C7B9] text-[#7A2E18]" :
              "bg-[#D4E0D1] text-[#1A3317]"
            }`}>{labelSvc(c.service_status)}</span>
          </Cell>
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">Profile & compliance</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Cell label="Customer type">{c.customer_type}</Cell>
          <Cell label="Vehicle access">{c.vehicle_access ? "Yes" : "No"}</Cell>
          <Cell label="Vehicle type">{labelVT(c.vehicle_type)}</Cell>
          <Cell label="Haritha Mithram ID">
            {c.haritha_mithram_id
              ? c.haritha_mithram_id
              : <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider bg-[#F4C7B9] text-[#7A2E18]">Need to fill</span>}
          </Cell>
          <Cell label="Property ownership">
            {c.property_ownership
              ? <span className="capitalize">{c.property_ownership}</span>
              : <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider bg-[#F4C7B9] text-[#7A2E18]">Need to fill</span>}
          </Cell>
          <Cell label="Registration certificate required">
            {c.registration_cert_required === true
              ? <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider bg-[#D4E0D1] text-[#1A3317]">Yes</span>
              : c.registration_cert_required === false
                ? <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider bg-[#E5E2D9] text-[#5C5A55]">No</span>
                : <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider bg-[#F4C7B9] text-[#7A2E18]">Need to fill</span>}
          </Cell>
          <Cell label="Total bags">{data.waste.total_bags}</Cell>
          <Cell label="Total waste (kg)">{data.waste.total_kg}</Cell>
          <Cell label="Open tickets">{data.complaints.filter(t => ["open","in_progress"].includes(t.status)).length}</Cell>
          <Cell label="Compliance score">{data.risk.score}/100</Cell>
        </div>
      </div>
    </div>
  );
};

const fmtMeta = (meta) => Object.entries(meta)
  .filter(([, v]) => v !== null && v !== "" && v !== false && typeof v !== "object")
  .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ");

const TimelineTab = ({ items }) => {
  const [limit, setLimit] = useState(50);
  if (items.length === 0) return <Empty icon={Pulse} text="No activity recorded yet." />;
  return (
  <div className="relative pl-6" data-testid="timeline-list">
    <div className="absolute left-[9px] top-2 bottom-2 w-px bg-[#E5E2D9]" />
    {items.slice(0, limit).map((a, i) => (
      <div key={a.id || i} className="relative pb-5">
        <div className="absolute -left-6 top-1.5 w-3 h-3 rounded-full bg-[#2D5A27] border-2 border-white ring-2 ring-[#D4E0D1]" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">{a.at ? new Date(a.at).toLocaleString() : ""}</div>
        <div className="font-medium text-sm text-[#1A1A1A] capitalize">{String(a.action || "").replace(/[._]/g, " ")}</div>
        {a.meta && fmtMeta(a.meta) && (
          <div className="text-xs text-[#5C5A55] mt-1">{fmtMeta(a.meta)}</div>
        )}
      </div>
    ))}
    {items.length > limit && (
      <button onClick={() => setLimit(l => l + 50)} data-testid="timeline-show-more"
              className="h-8 px-3 border border-[#E5E2D9] rounded-md text-xs font-semibold hover:bg-[#F7F6F2]">
        Show more ({items.length - limit} older)
      </button>
    )}
  </div>
  );
};

const ServicesTab = ({ services }) => {
  const { counts, items } = services;
  return (
    <div>
      <div className="grid grid-cols-5 gap-2 mb-5">
        <Tile label="Completed" value={counts.completed} tint="green" />
        <Tile label="In progress" value={counts.in_progress} />
        <Tile label="Pending" value={counts.pending} />
        <Tile label="Missed" value={counts.missed} tint="red" />
        <Tile label="Cancelled" value={counts.cancelled || 0} />
      </div>
      {items.length === 0 ? <Empty icon={Truck} text="No service history yet." /> : (
        <table className="w-full text-sm" data-testid="svc-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Request / Task</th>
              <th className="px-3 py-2">Staff</th>
              <th className="px-3 py-2">Invoice</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t, i) => (
              <tr key={t.id} className={i % 2 ? "bg-[#FAFAF7]" : "bg-white"} data-testid={`svc-row-${t.id}`}>
                <td className="px-3 py-2 font-mono">{t.date || "—"}</td>
                <td className="px-3 py-2 capitalize">{String(t.status || "pending").replace("_", " ")}</td>
                <td className="px-3 py-2 text-xs font-mono">
                  {t.request_no || (t.sequence ? `Task #${t.sequence}` : "Task")}
                  {(t.package_name || t.service_category) && <span className="ml-1 font-sans text-[#8A8780]">· {t.package_name || t.service_category}</span>}
                </td>
                <td className="px-3 py-2 text-xs text-[#5C5A55]">{t.assigned_staff_name || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono">{t.invoice_no || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const WasteTab = ({ waste }) => {
  const rows = Object.entries(waste.by_waste || {});
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Tile label="Total bags" value={waste.total_bags} />
        <Tile label="Total kg" value={waste.total_kg} />
        <Tile label="CO₂ saved (kg)" value={waste.total_co2_kg} tint="green" />
      </div>
      {rows.length === 0 ? <Empty icon={Leaf} text="No waste data collected yet." /> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-3 py-2">Waste type</th>
              <th className="px-3 py-2">Bags</th>
              <th className="px-3 py-2">Kg</th>
              <th className="px-3 py-2">CO₂ kg</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([name, v]) => (
              <tr key={name} className="border-b border-[#E5E2D9]">
                <td className="px-3 py-2">{name}</td>
                <td className="px-3 py-2 font-mono">{v.bags}</td>
                <td className="px-3 py-2 font-mono">{v.kg.toFixed(2)}</td>
                <td className="px-3 py-2 font-mono text-[#2D5A27]">{v.co2.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const PaymentsTab = ({ payments, customerId }) => {
  const [wallet, setWallet] = useState(null);
  const [walletTick, setWalletTick] = useState(0);
  useEffect(() => {
    if (!customerId) return;
    api.get(`/crm/customers/${customerId}/wallet`)
      .then(r => setWallet(r.data))
      .catch(() => setWallet(null));
  }, [customerId, walletTick]);

  const balance = wallet?.balance ?? 0;
  const txns = wallet?.txns || [];
  const belowMin = balance < 1000;

  return (
  <div>
    {/* Wallet Summary — Rescigo Red premium card */}
    <div className="mb-5 rounded-xl border border-[#E5E2D9] overflow-hidden" data-testid="cust-wallet-card">
      <div className={`px-5 py-4 flex items-center gap-4 ${belowMin ? "bg-gradient-to-r from-[#FEF2F2] to-[#FEE2E2]" : "bg-gradient-to-r from-[#FEF7F6] to-[#FDECEA]"}`}>
        <div className="w-12 h-12 rounded-full bg-white shadow-sm inline-flex items-center justify-center">
          <Coins size={22} weight="fill" className="text-[#DC2626]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-bold">Wallet Balance</div>
          <div className="text-3xl font-black text-[#DC2626]" data-testid="cust-wallet-balance">
            ₹{balance.toLocaleString("en-IN")}
          </div>
          {belowMin && (
            <div className="text-[11px] text-[#B91C1C] font-semibold mt-0.5">
              Below ₹1,000 — customer cannot book new service requests
            </div>
          )}
        </div>
        <Link
          to="/customers/wallet"
          className="h-9 px-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-bold inline-flex items-center gap-1"
          data-testid="cust-wallet-manage"
        >
          <ArrowSquareOut size={14} weight="bold" /> Manage
        </Link>
      </div>
      {txns.length > 0 && (
        <div className="bg-white">
          <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold">
            Wallet Transactions ({txns.length})
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-[#F0EEE7]">
            {txns.map(t => (
              <div key={t.id} className="px-5 py-2 flex items-center justify-between text-xs" data-testid={`cust-wallet-txn-${t.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-[#111827] capitalize truncate">{(t.kind || "").replace(/_/g, " ")}</div>
                  <div className="text-[10px] text-[#8A8780]">
                    {t.created_at ? new Date(t.created_at).toLocaleString() : ""}
                    {t.verified_by_name ? ` · verified by ${t.verified_by_name}` : ""}
                    {t.status && t.status !== "verified" ? ` · ${t.status}` : ""}
                    {t.refund_request_id ? ` · ${({ pg: "Payment gateway", cash: "Cash", upi: "UPI" })[t.refund_mode] || t.refund_mode}${t.transaction_id ? ` · Txn ${t.transaction_id}` : ""} · paid ${t.payment_date}` : ""}
                  </div>
                </div>
                <div className={`font-black shrink-0 ml-2 ${(t.amount || 0) >= 0 ? "text-[#15803D]" : "text-[#DC2626]"}`}>
                  {(t.amount || 0) >= 0 ? "+" : ""}₹{Math.abs(t.amount || 0).toLocaleString("en-IN")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {wallet && txns.length === 0 && (
        <div className="bg-white px-5 py-3 text-xs text-[#8A8780] italic">
          No wallet transactions yet.
        </div>
      )}
      {wallet && <WalletRefunds customerId={customerId} balance={balance} onChanged={() => setWalletTick(n => n + 1)} />}
    </div>

    <div className="grid grid-cols-4 gap-3 mb-5">
      <Tile label="Paid" value={`₹${payments.total_paid}`} sub={`${payments.paid_count} inv`} tint="green" />
      <Tile label="Outstanding" value={`₹${payments.total_due}`} sub={`${payments.unpaid_count} inv`} tint={payments.total_due > 0 ? "red" : undefined} />
      <Tile label="Transactions" value={payments.transactions.length} />
      <Tile label="Score" value={payments.total_due === 0 ? "A+" : payments.unpaid_count <= 1 ? "B" : "C"} />
    </div>
    {payments.invoices.length === 0 ? <Empty icon={Receipt} text="No invoices yet." /> : (
      <table className="w-full text-sm" data-testid="pay-table">
        <thead>
          <tr className="bg-[#F7F6F2] text-left text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <th className="px-3 py-2">Invoice</th>
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Amount</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {payments.invoices.map((inv, i) => (
            <tr key={inv.id} className={i % 2 ? "bg-[#FAFAF7]" : "bg-white"}>
              <td className="px-3 py-2 font-mono">{inv.invoice_no}</td>
              <td className="px-3 py-2">{inv.date}</td>
              <td className="px-3 py-2 font-mono">₹{Number(inv.total || 0).toFixed(2)}</td>
              <td className="px-3 py-2">
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${
                  inv.payment_status === "paid" ? "bg-[#D4E0D1] text-[#1A3317]"
                  : "bg-[#F4C7B9] text-[#7A2E18]"
                }`}>{inv.payment_status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
  );
};

const QrTab = ({ bags, customerId }) => {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!customerId) return;
    api.get(`/customers/${customerId}/bag-summary`).then(r => setSummary(r.data)).catch(() => {});
  }, [customerId]);
  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cust-bag-kpis">
          <KpiSmall label="Total Assigned" value={summary.total_assigned}/>
          <KpiSmall label="Current Balance" value={summary.current_balance} tone="#2D5A27"/>
          <KpiSmall label="Total Collected" value={summary.total_collected} tone="#5C5A55"/>
          <KpiSmall label="Pending Requests" value={summary.pending_requests} tone="#7A5A11"/>
        </div>
      )}
      {summary?.history?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">Bag History (Challans)</div>
          <table className="w-full text-sm" data-testid="cust-bag-history">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Challan</th>
                <th className="px-3 py-2">Range</th>
                <th className="px-3 py-2 text-right">Count</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.history.map((h, i) => (
                <tr key={h.id} className={i % 2 ? "bg-[#FAFAF7]" : "bg-white"}>
                  <td className="px-3 py-2 font-mono text-xs">{h.delivery_date}</td>
                  <td className="px-3 py-2 font-mono text-xs">{h.challan_no}</td>
                  <td className="px-3 py-2 font-mono text-xs">{h.first_code} → {h.last_code}</td>
                  <td className="px-3 py-2 text-right font-mono">{h.count}</td>
                  <td className="px-3 py-2 capitalize text-xs">{h.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {bags.length === 0 && <Empty icon={Package} text="No QR bags recorded yet."/>}
      {bags.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">Individual Bags</div>
          <table className="w-full text-sm" data-testid="qr-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-3 py-2">QR</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Weight</th>
                <th className="px-3 py-2">Collected</th>
              </tr>
            </thead>
            <tbody>
              {bags.map((b, i) => (
                <tr key={b.id} className={i % 2 ? "bg-[#FAFAF7]" : "bg-white"}>
                  <td className="px-3 py-2 font-mono">{b.qr_code}</td>
                  <td className="px-3 py-2 capitalize">{b.status}</td>
                  <td className="px-3 py-2 font-mono">{b.collected_weight_kg ?? b.weight_kg ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">{b.collected_at ? new Date(b.collected_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const KpiSmall = ({ label, value, tone = "#1A1A1A" }) => (
  <div className="bg-white border border-[#E5E2D9] rounded p-3">
    <div className="text-[9px] uppercase tracking-[0.18em] text-[#5C5A55]">{label}</div>
    <div className="font-mono font-semibold text-xl mt-0.5" style={{ color: tone }}>{value ?? 0}</div>
  </div>
);


const ComplaintsTab = ({ tickets, onOpen }) => (
  tickets.length === 0 ? <Empty icon={ChatsCircle} text="No complaints or tickets." /> : (
    <div className="space-y-2" data-testid="complaint-list">
      {tickets.map((t) => {
        // Iter 116 — mirror the CRM row blink policy: angry/very-bad
        // mood OR high priority AND still open → pulse the card so
        // support can act fast even from the customer profile.
        const hot = t.status !== "closed" && t.status !== "resolved" && (
          t.mood === "angry" || t.mood === "very_bad" || t.priority === "high"
        );
        return (
          <div key={t.id}
               data-testid={`complaint-item-${t.id}`}
               className={`border border-[#E5E2D9] rounded-md p-3 ${hot ? "tkt-blink-row" : ""}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-medium">{t.ticket_no}</span>
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${
                t.status === "resolved" ? "bg-[#D4E0D1]" :
                t.status === "in_progress" ? "bg-[#F9E4B7]" :
                t.status === "closed" ? "bg-[#E5E2D9]" : "bg-[#F4C7B9]"
              }`}>{t.status}</span>
              <span className="text-[10px] text-[#8A8780] uppercase tracking-[0.14em]">
                {t.priority}
              </span>
              {t.mood && t.mood !== "normal" && (
                <span className="text-[10px] font-medium text-[#7A2E18] uppercase tracking-[0.14em]">
                  {t.mood.replace(/_/g," ")}
                </span>
              )}
              <span className="text-[10px] text-[#8A8780] ml-auto">{new Date(t.created_at).toLocaleDateString()}</span>
              {onOpen && (
                <button type="button"
                        onClick={() => onOpen(t)}
                        data-testid={`complaint-open-${t.id}`}
                        title="Open ticket · edit + reply"
                        className="h-7 px-2 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] text-white text-[10px] uppercase tracking-[0.14em] font-semibold inline-flex items-center gap-1">
                  <TicketIcon size={11} weight="fill"/>Open
                </button>
              )}
            </div>
            <div className="text-sm text-[#1A1A1A] mt-1">{t.issue}</div>
          </div>
        );
      })}
    </div>
  )
);

const RiskTab = ({ risk }) => (
  <div>
    <div className="grid grid-cols-3 gap-3 mb-5">
      <div className={`rounded-md p-4 ${risk.score >= 80 ? "bg-[#D4E0D1]" : risk.score >= 50 ? "bg-[#F9E4B7]" : "bg-[#F4C7B9]"}`}>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Compliance score</div>
        <div className="font-heading text-3xl" data-testid="risk-score">{risk.score}<span className="text-sm opacity-60">/100</span></div>
      </div>
      <Tile label="Open alerts" value={risk.open_alerts} tint={risk.open_alerts > 0 ? "red" : undefined} />
      <Tile label="All alerts" value={risk.alerts.length} />
    </div>
    {risk.alerts.length === 0 ? <Empty icon={ShieldCheck} text="No risk alerts — clean record." /> : (
      <div className="space-y-2" data-testid="risk-alert-list">
        {risk.alerts.map((a) => (
          <div key={a.id} className="border border-[#E5E2D9] rounded-md p-3 flex items-start gap-2">
            <Warning size={16} weight="fill"
                     color={a.severity === "high" ? "#C25D38" : a.severity === "medium" ? "#D49A44" : "#8A8780"} />
            <div className="flex-1">
              <div className="text-sm font-medium">{a.type || a.alert_type || "Alert"}</div>
              <div className="text-xs text-[#5C5A55]">{a.message}</div>
            </div>
            <div className="text-[10px] text-[#8A8780]">{new Date(a.created_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const RewardsTab = ({ customerId }) => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    api.get(`/customers/${customerId}/rewards`).then(r => setD(r.data)).catch(() => setD({ error: true }));
  }, [customerId]);
  if (!d) return <div className="text-sm text-[#5C5A55] py-6 text-center">Loading rewards…</div>;
  if (d.error) return <Empty icon={Coins} text="Couldn't load rewards data." />;
  const w = d.wallet;
  return (
    <div className="space-y-5" data-testid="rewards-tab">
      <div className="grid grid-cols-4 gap-3">
        <Tile label="Earned" value={`₹${Number(w.total_earned||0).toFixed(2)}`} />
        <Tile label="Available" value={`₹${Number(w.available||0).toFixed(2)}`} tint={d.payout_enabled ? "green" : undefined} />
        <Tile label="Paid out" value={`₹${Number(w.total_paid||0).toFixed(2)}`} />
        <Tile label="Referrals" value={d.referred_customers.length} />
      </div>
      <div className="flex items-center gap-2">
        <Link to={`/customers/${customerId}/rewards`}
              data-testid="open-full-rewards"
              className="h-9 px-3 inline-flex items-center gap-2 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-xs font-medium">
          <Coins size={12} weight="bold"/>Open full Rewards page
        </Link>
        <span className="text-[11px] text-[#8A8780]">Manual payout · ledger · referrals</span>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Recent referral credits</div>
        {d.referrals.length === 0 ? <Empty icon={Coins} text="No reward credits yet." /> : (
          <div className="border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="rewards-recent">
            {d.referrals.slice(0, 5).map(r => (
              <div key={r.id} className="px-4 py-2.5 border-b border-[#F0ECE3] last:border-0 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="truncate">{r.referred_customer_name}</div>
                  <div className="text-[11px] font-mono text-[#8A8780]">{r.invoice_no} · {(r.rewarded_at||"").slice(0,10)}</div>
                </div>
                <div className="font-mono text-[#2D5A27]">+₹{Number(r.reward_amount).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Referred customers ({d.referred_customers.length})</div>
        {d.referred_customers.length === 0 ? <Empty icon={User} text="No customers referred yet." /> : (
          <div className="border border-[#E5E2D9] rounded-md overflow-hidden">
            {d.referred_customers.slice(0, 10).map(c => (
              <Link key={c.id} to={`/customers/${c.id}`}
                    className="flex items-center gap-3 px-4 py-2.5 border-b border-[#F0ECE3] last:border-0 text-sm hover:bg-[#F7F6F2]">
                <span className="flex-1 truncate">{c.name}</span>
                <span className="font-mono text-[11px] text-[#5C5A55]">{c.customer_code}</span>
                <span className={`text-[9px] px-2 py-0.5 rounded uppercase tracking-wider ${c.status === "active" || c.status === "approved" ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#F9E4B7] text-[#7A5A11]"}`}>{c.status}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const CommsTab = ({ items }) => (
  items.length === 0 ? <Empty icon={Envelope} text="No communication logs yet. SMS/Email records will appear here." /> : (
    <div className="space-y-2">
      {items.map((m) => (
        <div key={m.id} className="border border-[#E5E2D9] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">{m.channel} · {new Date(m.created_at).toLocaleString()}</div>
          <div className="text-sm mt-1">{m.content}</div>
        </div>
      ))}
    </div>
  )
);

const NotesTab = ({ customerId, notes, reload }) => {
  const [body, setBody] = useState("");
  const [tag, setTag] = useState("note");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/customers/${customerId}/notes`, { body: body.trim(), tag });
      toast.success("Note added");
      setBody("");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const del = async (nid) => {
    if (!window.confirm("Delete note?")) return;
    try { await api.delete(`/customers/${customerId}/notes/${nid}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <form onSubmit={add} className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-3 mb-5 flex items-start gap-2" data-testid="note-form">
        <select value={tag} onChange={(e) => setTag(e.target.value)}
                className="h-10 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
          <option value="note">Note</option>
          <option value="action">Action</option>
          <option value="followup">Follow-up</option>
        </select>
        <input value={body} onChange={(e) => setBody(e.target.value)}
               placeholder="Write a note or next action…"
               data-testid="note-body"
               className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white" />
        <button type="submit" disabled={busy || !body.trim()} data-testid="note-save"
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium inline-flex items-center gap-1.5">
          <Plus size={12} weight="bold" />Add
        </button>
      </form>
      {notes.length === 0 ? <Empty icon={Note} text="No notes yet. Add the first one above." /> : (
        <div className="space-y-2" data-testid="note-list">
          {notes.map((n) => (
            <div key={n.id} className="border border-[#E5E2D9] rounded-md p-3 flex items-start gap-3">
              <div className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${
                n.tag === "action" ? "bg-[#F9E4B7] text-[#7A5A11]" :
                n.tag === "followup" ? "bg-[#F4C7B9] text-[#7A2E18]" :
                "bg-[#D4E0D1] text-[#1A3317]"
              }`}>{n.tag}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#1A1A1A]">{n.body}</div>
                <div className="text-[10px] text-[#8A8780] mt-1">
                  {n.author_name} · {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
              <button onClick={() => del(n.id)} data-testid={`note-del-${n.id}`}
                      className="p-1.5 text-[#C25D38] hover:bg-[#C25D38]/8 rounded"><Trash size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Shared ----------

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded bg-[#F7F6F2] flex items-center justify-center shrink-0">
      <Icon size={14} color="#2D5A27" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
      <div className="text-[#1A1A1A] text-sm break-words">{value}</div>
    </div>
  </div>
);

const Cell = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
    <div className="mt-1 text-[#1A1A1A]">{children}</div>
  </div>
);

const Kpi = ({ label, value, sub, danger }) => (
  <div>
    <div className={`font-heading text-xl ${danger ? "text-[#C25D38]" : "text-[#1A1A1A]"}`}>{value}</div>
    <div className="text-[9px] uppercase tracking-[0.18em] text-[#8A8780]">{label}</div>
    {sub && <div className="text-[10px] text-[#5C5A55]">{sub}</div>}
  </div>
);

const Tile = ({ label, value, sub, tint }) => {
  const bg = tint === "green" ? "bg-[#D4E0D1]" : tint === "red" ? "bg-[#F4C7B9]" : "bg-[#F7F6F2]";
  return (
    <div className={`${bg} border border-[#E5E2D9] rounded-md p-3`}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C5A55]">{label}</div>
      <div className="font-heading text-xl mt-1">{value}</div>
      {sub && <div className="text-[10px] text-[#8A8780]">{sub}</div>}
    </div>
  );
};

const Empty = ({ icon: Icon, text }) => (
  <div className="py-14 text-center text-sm text-[#8A8780]">
    <Icon size={28} className="inline-block mb-3 opacity-40" weight="duotone" />
    <div>{text}</div>
  </div>
);
